import { useCallback, useEffect, useRef, useState } from "react";

import { IconPlusCircle, IconRefreshCw } from "@frontend/common";

import * as S from "./AIAdvisorTableBodyCell.style";
import AIAdvisorCellCopyButton from "./components/AIAdvisorCellCopyButton/AIAdvisorCellCopyButton";
import { useHandleDrugProfileGroupShell } from "./hooks/useAddDrugProfileGroupShell";
import { useChangeDrugProfileGroupShell } from "./hooks/useChangeDrugProfileGroupShell";

const DefaultValue = () => {
  return <S.BodyCellValue $isDisplayBullet={false}>-</S.BodyCellValue>;
};

type Props = {
  value: string[];
  maxWidth?: string;
  minWidth?: string;
  tooltipAlignment: "left" | "center" | "right";
  hasCopyButton?: boolean;
  hasChangeButton?: boolean;
  drugKey?: string | "";
  drugIndex?: number;
  changedValue?: string;
  isDisableEdit?: boolean;
};

const AIAdvisorTableBodyCell = ({
  value,
  maxWidth,
  minWidth,
  tooltipAlignment,
  hasCopyButton = false,
  hasChangeButton = false,
  drugKey,
  drugIndex,
  changedValue,
  isDisableEdit,
}: Props) => {
  const bodyCellRef = useRef<HTMLDivElement>(null);
  const bodyCellValueRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { handleAddDrugProfileGroupShell, isDuplicateValue } =
    useHandleDrugProfileGroupShell({
      positionPropsInfo: {
        groupTableKey: "Drug Profile",
        groupTableIndex: drugIndex || 0,
        groupShellKey: drugKey || null,
        groupShellIndex: null,
        tableKey: null,
        tableIndex: drugIndex || 0,
      },
    });

  const { handleChangeDrugProfileGroupShell, isDuplicatedValue } =
    useChangeDrugProfileGroupShell({
      positionPropsInfo: {
        groupTableKey: "Drug Profile",
        groupTableIndex: drugIndex || 0,
        groupShellKey: drugKey || null,
        groupShellIndex: null,
        tableKey: null,
        tableIndex: drugIndex || 0,
      },
    });

  const [isHovered, setIsHovered] = useState(false);
  const [isEllipsisActive, setIsEllipsisActive] = useState(false);
  const [currentTooltipPosition, setCurrentTooltipPosition] = useState({
    top: 0,
    left: 0,
  });

  const checkEllipsis = useCallback(() => {
    const visibleItems = value.slice(0, 3).filter((item) => item);
    const hasEllipsis = visibleItems.some((_, index) => {
      const element = bodyCellValueRefs.current[index];
      if (element) {
        const isHorizontalOverflow = element.scrollWidth > element.clientWidth;
        const isVerticalOverflow = element.scrollHeight > element.clientHeight;
        return isHorizontalOverflow || isVerticalOverflow;
      }
      return false;
    });

    setIsEllipsisActive(hasEllipsis);
  }, [value]);

  const calculateTooltipPosition = useCallback(() => {
    if (!bodyCellRef.current || !tooltipRef.current) return;

    const cellRect = bodyCellRef.current.getBoundingClientRect();
    const tooltipHeight = tooltipRef.current.offsetHeight;
    let left = 0;

    if (tooltipAlignment === "left") {
      left = cellRect.left;
    } else if (tooltipAlignment === "right") {
      left = cellRect.left + cellRect.width;
    } else {
      left = cellRect.left + cellRect.width / 2;
    }

    const top = cellRect.top - tooltipHeight;

    setCurrentTooltipPosition({
      top,
      left,
    });
  }, [tooltipAlignment]);

  useEffect(() => {
    bodyCellValueRefs.current = bodyCellValueRefs.current.slice(
      0,
      value.length,
    );
  }, [value]);

  useEffect(() => {
    checkEllipsis();
    calculateTooltipPosition();

    const handleResize = () => {
      checkEllipsis();
      calculateTooltipPosition();
    };

    const handleScroll = () => {
      if (isHovered) {
        calculateTooltipPosition();
      }
    };

    window.addEventListener("resize", handleResize);

    const bottomSheet = document.getElementById("ai-advisor-bottom-sheet");
    if (bottomSheet) {
      bottomSheet.addEventListener("scroll", handleScroll);
    }

    return () => {
      window.removeEventListener("resize", handleResize);

      if (bottomSheet) {
        bottomSheet.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isEllipsisActive, calculateTooltipPosition, isHovered, checkEllipsis]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    calculateTooltipPosition();
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const isTooltipVisible = isHovered && (isEllipsisActive || value.length > 3);

  const isDisplayBullet = value.length > 1;

  const remainingValueCount = value.length - 3;

  const sliceValueList = value.slice(0, 3);

  const clickValueChangeButton = (changed: string) => {
    if (!isDuplicatedValue(changed || "") && !isDisableEdit) {
      handleChangeDrugProfileGroupShell(changed || "");
    }
  };

  const clickValueAddButton = (item: string) => {
    if (drugKey && !isDuplicateValue(item) && !isDisableEdit) {
      handleAddDrugProfileGroupShell(item || "");
    }
  };

  const currentIconColorStatus = (isDisabled: boolean) =>
    isDisabled ? "#96A1B0" : "#515C6C";

  return (
    <S.BodyCell
      $maxWidth={maxWidth}
      $minWidth={minWidth}
      $isTooltipVisible={isTooltipVisible}
      ref={bodyCellRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      $isDisableEdit={isDisableEdit}
    >
      {sliceValueList.map((item, index) =>
        item ? (
          <S.ListRow key={`cell-${item}`} $isDisplayBullet={isDisplayBullet}>
            <S.BodyCellValue
              ref={(el) => {
                bodyCellValueRefs.current[index] = el;
              }}
              $isDisplayBullet={isDisplayBullet}
              onMouseEnter={handleMouseEnter}
              $isDisableEdit={isDisableEdit}
            >
              {item}
            </S.BodyCellValue>
            {hasChangeButton && (
              <S.IconWrapper
                $disabled={
                  isDuplicatedValue(changedValue || "") || !!isDisableEdit
                }
                onClick={() => clickValueChangeButton(changedValue || "")}
              >
                <IconRefreshCw
                  width={20}
                  height={20}
                  color={currentIconColorStatus(
                    isDuplicatedValue(changedValue || "") || !!isDisableEdit,
                  )}
                />
              </S.IconWrapper>
            )}
            {hasCopyButton && (
              <>
                <AIAdvisorCellCopyButton
                  textValue={item}
                  isDisableEdit={isDisableEdit}
                />
                <S.IconWrapper
                  $disabled={isDuplicateValue(item) || isDisableEdit}
                  onClick={() => clickValueAddButton(item)}
                >
                  <IconPlusCircle
                    width={20}
                    height={20}
                    color={currentIconColorStatus(
                      isDuplicateValue(item) || !!isDisableEdit,
                    )}
                  />
                </S.IconWrapper>
              </>
            )}
          </S.ListRow>
        ) : (
          <DefaultValue key={`default-${drugIndex}`} />
        ),
      )}
      {remainingValueCount > 0 && (
        <S.CountText>+{remainingValueCount}</S.CountText>
      )}
      <S.Tooltip
        ref={tooltipRef}
        $tooltipAlignment={tooltipAlignment}
        $currentTooltipPosition={currentTooltipPosition}
        $isTooltipVisible={isTooltipVisible && !isDisableEdit}
      >
        {value.map((item) => (
          <S.ListRow key={`tooltip-${item}`} $isDisplayBullet={isDisplayBullet}>
            <S.TooltipValue $isDisplayBullet={isDisplayBullet}>
              {item}
            </S.TooltipValue>
            {hasCopyButton && (
              <S.ToolTipIconWrapper>
                <AIAdvisorCellCopyButton textValue={item} />
                <S.IconWrapper
                  onClick={() => clickValueAddButton(item)}
                  $disabled={isDuplicateValue(item)}
                >
                  <IconPlusCircle
                    width={20}
                    height={20}
                    color={currentIconColorStatus(isDuplicateValue(item))}
                  />
                </S.IconWrapper>
              </S.ToolTipIconWrapper>
            )}
          </S.ListRow>
        ))}
      </S.Tooltip>
    </S.BodyCell>
  );
};

export default AIAdvisorTableBodyCell;
